import { apiService } from './api';
import { User } from '../contexts/AuthContext';

interface LoginResponse {
  success: boolean;
  token?: string;
  user?: User;
  requires2FA?: boolean;
  tempToken?: string;
  message?: string;
}

interface UserResponse {
  success: boolean;
  user: User;
}

class AuthService {
  async loginAdmin(email: string, password: string): Promise<{ user?: User; token?: string; requires2FA?: boolean; tempToken?: string }> {
    const response = await apiService.post<LoginResponse>('/auth/login-admin', {
      email,
      password,
    });

    if (!response.success) {
      throw new Error('Login failed');
    }

    // Check if 2FA is required
    if (response.requires2FA && response.tempToken) {
      return {
        requires2FA: true,
        tempToken: response.tempToken,
      };
    }

    return {
      user: response.user,
      token: response.token,
    };
  }

  async loginPublisher(
    email: string,
    password: string,
    recaptchaToken?: string,
  ): Promise<{ user?: User; token?: string; requires2FA?: boolean; tempToken?: string }> {
    const response = await apiService.post<LoginResponse>('/auth/login-publisher', {
      email,
      password,
      recaptchaToken,
    });

    if (!response.success) {
      throw new Error('Login failed');
    }

    // Check if 2FA is required
    if (response.requires2FA && response.tempToken) {
      return {
        requires2FA: true,
        tempToken: response.tempToken,
      };
    }

    return {
      user: response.user,
      token: response.token,
    };
  }

  async loginClient(email: string, password: string): Promise<{ user?: User; token?: string; requires2FA?: boolean; tempToken?: string }> {
    const response = await apiService.post<LoginResponse>('/auth/login-client', {
      email,
      password,
    });

    if (!response.success) {
      throw new Error('Login failed');
    }

    if (response.requires2FA && response.tempToken) {
      return {
        requires2FA: true,
        tempToken: response.tempToken,
      };
    }

    return {
      user: response.user,
      token: response.token,
    };
  }

  async loginAgency(email: string, password: string): Promise<{ user?: User; token?: string; requires2FA?: boolean; tempToken?: string }> {
    const response = await apiService.post<LoginResponse>('/auth/login-agency', {
      email,
      password,
    });

    if (!response.success) {
      throw new Error('Login failed');
    }

    if (response.requires2FA && response.tempToken) {
      return {
        requires2FA: true,
        tempToken: response.tempToken,
      };
    }

    return {
      user: response.user,
      token: response.token,
    };
  }

  async loginPortal(
    email: string,
    password: string,
    recaptchaToken?: string,
  ): Promise<{ user?: User; token?: string; requires2FA?: boolean; tempToken?: string }> {
    const response = await apiService.post<LoginResponse>('/auth/login-portal', {
      email,
      password,
      recaptchaToken,
    });

    if (!response.success) {
      throw new Error('Login failed');
    }

    if (response.requires2FA && response.tempToken) {
      return {
        requires2FA: true,
        tempToken: response.tempToken,
      };
    }

    return {
      user: response.user,
      token: response.token,
    };
  }

  async login(email: string, password: string): Promise<{ user?: User; token?: string; requires2FA?: boolean; tempToken?: string }> {
    const response = await apiService.post<LoginResponse>('/auth/login', {
      email,
      password,
    });

    if (!response.success) {
      throw new Error('Login failed');
    }

    // Check if 2FA is required
    if (response.requires2FA && response.tempToken) {
      return {
        requires2FA: true,
        tempToken: response.tempToken,
      };
    }

    return {
      user: response.user,
      token: response.token,
    };
  }

  async verify2FA(tempToken: string, code: string): Promise<{ user: User; token: string }> {
    const response = await apiService.post<LoginResponse>('/2fa/verify-login', {
      tempToken,
      code,
    });

    if (!response.success || !response.user || !response.token) {
      throw new Error('2FA verification failed');
    }

    return {
      user: response.user,
      token: response.token,
    };
  }

  async getCurrentUser(): Promise<User> {
    const response = await apiService.get<UserResponse>('/auth/me');
    
    if (!response.success) {
      throw new Error('Failed to get user data');
    }

    return response.user;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await apiService.put('/auth/change-password', {
      currentPassword,
      newPassword,
    });
  }

  async updateProfile(data: {
    firstName?: string;
    lastName?: string;
    email?: string;
    organizationName?: string | null;
    organizationUrl?: string | null;
    organizationDescription?: string | null;
    avatarUrl?: string | null;
    organizationLogoUrl?: string | null;
  }): Promise<User> {
    const response = await apiService.put<{ success: boolean; user: User; error?: string }>(
      '/auth/profile',
      data
    );
    if (!response.success || !response.user) {
      throw new Error(response.error || 'Failed to update profile');
    }
    return response.user;
  }

  async uploadAvatar(file: File): Promise<{ avatarUrl: string; user: User }> {
    const form = new FormData();
    form.append('avatar', file);
    const response = await apiService.upload<{
      success: boolean;
      avatarUrl: string;
      user: User;
      error?: string;
    }>('/auth/profile/avatar', form);
    if (!response.success) {
      throw new Error(response.error || 'Failed to upload avatar');
    }
    return { avatarUrl: response.avatarUrl, user: response.user };
  }

  async uploadOrganizationLogo(file: File): Promise<{ organizationLogoUrl: string; user: User }> {
    const form = new FormData();
    form.append('logo', file);
    const response = await apiService.upload<{
      success: boolean;
      organizationLogoUrl: string;
      user: User;
      error?: string;
    }>('/auth/profile/logo', form);
    if (!response.success) {
      throw new Error(response.error || 'Failed to upload logo');
    }
    return { organizationLogoUrl: response.organizationLogoUrl, user: response.user };
  }

  async verifyPassword(password: string): Promise<void> {
    const response = await apiService.post<{ success: boolean; message?: string }>('/auth/verify-password', {
      password,
    });

    if (!response.success) {
      throw new Error('Password verification failed');
    }
  }

  async verifySensitivePagePassword(password: string): Promise<void> {
    const response = await apiService.post<{ success: boolean; message?: string }>('/auth/verify-sensitive-password', {
      password,
    });

    if (!response.success) {
      throw new Error('Sensitive page password verification failed');
    }
  }

  async getRecaptchaConfig(): Promise<{ siteKey: string; required: boolean }> {
    const response = await apiService.get<{ success: boolean; siteKey: string; required: boolean }>(
      '/auth/recaptcha-config',
    );
    return { siteKey: response.siteKey || '', required: Boolean(response.required) };
  }

  async register(data: {
    accountType: 'client' | 'agency';
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    company?: string;
    country?: string;
    phone?: string;
    recaptchaToken?: string;
  }): Promise<{ success: boolean; message?: string; pendingVerification?: boolean }> {
    return apiService.post('/auth/register', data);
  }

  async verifyEmail(token: string): Promise<{ success: boolean; message?: string; alreadyVerified?: boolean }> {
    return apiService.post('/auth/verify-email', { token });
  }

  async resendVerification(
    email: string,
    recaptchaToken?: string,
  ): Promise<{ success: boolean; message?: string }> {
    return apiService.post('/auth/resend-verification', { email, recaptchaToken });
  }

  async updateSensitivePagePassword(loginPassword: string, newSensitivePassword: string): Promise<void> {
    const response = await apiService.put<{ success: boolean; message?: string }>('/auth/sensitive-page-password', {
      loginPassword,
      newSensitivePassword,
    });

    if (!response.success) {
      throw new Error('Failed to update sensitive page password');
    }
  }
}

export const authService = new AuthService();
